import exp from "constants";
import { ApiUtility } from "../Utilities/ApiUtility.axios";

export interface IPracticeAddress {
  building: string;
  city: string;
  country: string;
  county: string;
  postcode: string;
  street: string;
}

export interface IPracticeParams {
  dueInvoicesTaskDays: number;
  pageTrackTimeout: number;
  taskDueDateInDays: number;
  secureLinkExpiry: number;
}

export interface IPracticeAppFeature {
  accounts: boolean;
  aml: boolean;
  bankStatement: boolean;
  bookkeeping: boolean;
  calls: boolean;
  chats: boolean;
  clientDataRequest: boolean;
  corporationTax: boolean;
  dayEnd: boolean;
  leadSources: boolean;
  linkedinLeads: boolean;
  payroll: boolean;
  teams: boolean;
  webForms: boolean;
  timeSheet: boolean;
  careOf: boolean;
  selfAssessment: boolean;
  dms: boolean;
  audit: boolean,
  companiesHouseSync: boolean,
  other1: boolean,
  other2: boolean,
  other3: boolean,
}

export interface IPracticeAppSubFeature {
  digitalSignatureWithoutAML: boolean;
  websiteQueriesWithOTP: boolean;
  preparationSideValidation: boolean;
  cardNet: boolean;
  manualTimesheet: boolean;
  progressiveLeads: boolean;
  withoutDiscount: boolean;
  actionTab: boolean;
  scheduleEmail: boolean;
  resourceSection: boolean;
  clientAutomation: boolean;
  proofOfEarningFPS: boolean;
  receiptAttachments: boolean;
  skipTeamUpdate: boolean;
  secureLink: boolean;
  bankDetails: boolean;
  amLforAllOfficers: boolean;
  firstResponse: boolean;
  accountManagar: boolean;
  accountant: boolean;
  mode: boolean;
  agentCredentialsRemark: boolean;
  clientBrand: boolean;
  s455TaxLiability: boolean;
  accaBotChat: boolean;
  noChangeInTaskAssigneeWithTeamChange: boolean;
  directorAccountWhileRetrieve: boolean;
  reviewNotificationToTeamleader: boolean;
  allowTLandAdminToUpdateTaskDeadline: boolean;
  loeFinancialAutomation: boolean;
  collectionMethod: boolean;
  chargeDay: boolean;
  clientsJoinersAndLeavers: boolean;
  blockInvoiceEdit: boolean;
  masking: boolean;
  xcelify: boolean;
  clientReferenceInInvoice: boolean;
  payeMasterReport: boolean;
  addCustomer: boolean;
  teamSalesComparison: boolean;
  creditNoteReviewRequest: boolean;
  reviewAutoRefund: boolean;
  /* ===== Advanced / AI / Automation ===== */
  biometric: boolean;
  reviewRequestAttachment: boolean;
  accaBot: boolean;
  autoComplete: boolean;
  emailSummarizer: boolean;
  bookOcrText: boolean;
  callTranscription: boolean;
  teamGroup: boolean;
  teamIsolation: boolean;



  /* ===== Nested Config Objects (USED) ===== */
  mtsAutomationSettings: {
    automate: boolean;
    days: number;
  };

  noReply: {
    enabled: boolean;
    replyEmail: string;
  };

  reviewRequestConfig: {
    APCT: boolean;
    SA100: boolean;
    SA800: boolean;
    VAT: boolean;
  };

  /* ===== NOT USED / NOT BACKEND SUPPORTED (COMMENTED) ===== */
  // addCustomerWithoutLead: boolean;
  // soaIsEnable: boolean;
  // soaIsNotificationEnable: boolean;
  // clientProposition: boolean;
  // clientCredentials: boolean;
  // payeBulkEPS: boolean;
  // payeServiceRequest: boolean;
}


export interface IPracticeBankAccount {
  accountName: string;
  accountNumber: string;
  bankName: string;
  bik: string;
  iban: string;
  shortCode: string;
}

export interface IPracticeWebsite {
  iconUrl: string;
  logoUrl: string;
  url: string;
  urlPrivacy: string;
  urlTnC: string;
}

export enum PracticeSequenceTypes {
  None = 1,
  Yearly = 2,
  Monthly = 3,
}

export interface IPracticeSequence {
  type: PracticeSequenceTypes;
  prefix: string;
  postfix: string;
  seperator: string;
  leadingZero: number;
}
export interface IPracticeBadge {
  warningMessage: string,
  dateFrom: string,
  dateTo: string,
  sA100: boolean,
  sA800: boolean,
  cis: boolean,
  vat: boolean,
  eps: boolean,
  fps: boolean,
  confirmationStatement: boolean,
  corporationTax: boolean,
  accountsReport: boolean,
}
export interface IPractice {
  id: any;
  address: IPracticeAddress;
  params: IPracticeParams;
  appFeature: IPracticeAppFeature;
  appSubFeature: IPracticeAppSubFeature;
  bankAccount: IPracticeBankAccount;
  website: IPracticeWebsite;
  name: string;
  email: string;
  phoneNumber: number;
  timeZone: string;
  vat: {
    enabled: boolean;
    rate: number;
  };
  emailProvider: number;
  defaultBCCs: any[];
  defaultCCs: any[];
  sequences: {
    invoice: IPracticeSequence;
    quote: IPracticeSequence;
    creditNote: IPracticeSequence;
    receipt: IPracticeSequence;
    refund: IPracticeSequence;
  };
  badges: IPracticeBadge;
}

export const enum PracticeUpdateTypes {
  unknown = 0,
  generalSettings = 1,
  basicInformation = 2,
  address = 3,
  bankAccountDetails = 4,
  timeZoneVat = 5,
  emails = 6,
  praticeProperites = 7,
  praticeSequences = 8,
  features = 9,
  reviewRequest = 10,
  badges = 11,
}

export const enum PracticeTabsType {
  unknown = "0",
  generalSettings = "1",
  basicInformation = "2",
  bankAccountDetails = "3",
  timeZoneVatEmail = "4",
  sequenceAndReview = "5",
  features = "6",
  submissionHold = "7",
  aml = "8",
  biometric = "9",
}


export const PracticeTabsTypeList = ["Unknown", "Basic Details", "Bank Account", "Time zone, Vat & Email", "Sequence & Review", "Features", "Submission Hold", "AML", "AML-Biometric"]


export const enum PracticeStatus {
  Active = 0,
  InActive = 1,
}

export interface IGeneralSettingsInitialValues {
  name: string;
  email: string;
  logoUrl: string;
  connectionString: string;
  database: string;
  host: string;
  identifier: string;
}

export interface IPracticeBasicInformationInitialValues {
  name: string;
  email: string;
  phoneNumber: number;
  website: IPracticeWebsite
}

export interface ITimeZoneVatInitialValues {
  timeZone: string;
  vat: {
    enabled: boolean;
    rate: number;
  };
}
export interface IPracticeEmailInitialValues {
  defaultBCCs: string[];
  defaultCCs: string[];
  emailProvider: number;
  noReply: {
    enabled: boolean;
    replyEmail: string;
  };
}

export interface ISequenceReviewInitialValues {
  invoice: IPracticeSequence;
  quote: IPracticeSequence;
  creditNote: IPracticeSequence;
  receipt: IPracticeSequence;
  refund: IPracticeSequence;
}

export interface ISequenceReviewFormValues {
  sequences: {
    invoice: IPracticeSequence;
    quote: IPracticeSequence;
    creditNote: IPracticeSequence;
    receipt: IPracticeSequence;
    refund: IPracticeSequence;
  };
  appSubFeature: {
    reviewRequestConfig: {
      apct: boolean;
      sA100: boolean;
      sA800: boolean;
      vat: boolean;
    };
  };
}

export interface ISequenceReviewInitialValuesType {
  invoice: IPracticeSequence;
  quote: IPracticeSequence;
  creditNote: IPracticeSequence;
  receipt: IPracticeSequence;
  refund: IPracticeSequence;
  reviewRequestConfig: {
    APCT: boolean;
    SA100: boolean;
    SA800: boolean;
    VAT: boolean;
  };
}
export enum ClientReviewerUserOptionsType {
  Unknown = 0,
  Accountant = 1,
  AccountManager = 2,
}
export enum DefaultMissCallAgent {
  Unknown = 0,
  TeamLeader = 1,
  FirstResponse = 2,
  AccountManager = 3,
  Accountant = 4,
}

export const ClientReviewerUserOptionsTypeList = [
  { label: "None", value: ClientReviewerUserOptionsType.Unknown },
  { label: "Accountant", value: ClientReviewerUserOptionsType.Accountant },
  { label: "Account Manager", value: ClientReviewerUserOptionsType.AccountManager },
];


export const DefaultMissCallAgentTypeList = [
  { label: "None", value: DefaultMissCallAgent.Unknown },
  { label: "Team leader", value: DefaultMissCallAgent.TeamLeader },
  { label: "First response", value: DefaultMissCallAgent.FirstResponse },
  { label: "Account manager", value: DefaultMissCallAgent.AccountManager },
  { label: "Accountant", value: DefaultMissCallAgent.Accountant }
]

class PracticesService {
  route = `/Api/Practices`;
  // Get Practices List
  // start=${start}&length=${length}&search=${search}&sortDir=${sortDir}&sortCol=${sortCol}
  getPracticesList = (
    start: number,
    length: any,
    search: string,
    sortCol: any,
    sortDir: any,
    status?: PracticeStatus,
  ) =>
    ApiUtility.get(`${this.route}`, {
      start,
      length,
      search,
      sortDir,
      sortCol,
      status,
    });


  getPracticeDetails = (id: any) => ApiUtility.get(`${this.route}/${id}`);

  getPracticeGeneralSettings = (id: number) => ApiUtility.get(`${this.route}/${id}/generalSettings`);

  updatePracticeGeneralSettings = (
    id: number,
    params: { name: string; connectionString: string; database: string; host: string; identifier: string; },
  ) => ApiUtility.post(`${this.route}/${id}/updateGeneralSettings`, params);

  getPracticeBasicInformation = (id: number) => ApiUtility.get(`${this.route}/${id}/basicInformation`);

  updatePracticeBasicInformation = (id: number, params: IPracticeBasicInformationInitialValues) => ApiUtility.post(`${this.route}/${id}/updateBasicInfo`, params);

  updatePracticeAddressInformation = (id: number, params: IPracticeAddress) => ApiUtility.post(`${this.route}/${id}/updateAddressInfo`, params);

  getPracticeTimeVatEmailInformation = (id: number) => ApiUtility.get(`${this.route}/${id}/timeVatEmail`);

  updatePracticeTimezoneVate = (id: number, params: ITimeZoneVatInitialValues) => ApiUtility.post(`${this.route}/${id}/updateTimeVat`, params);

  updatePracticeEmail = (id: number, params: IPracticeEmailInitialValues) => ApiUtility.post(`${this.route}/${id}/updateEmail`, params);

  getPracticeSequenceReviewDetails = (id: number) => ApiUtility.get(`${this.route}/${id}/sequenceReview`);

  updatePracticeSequence = (id: number, params: ISequenceReviewInitialValues) => ApiUtility.post(`${this.route}/${id}/updateSequence`, params);
  updatePracticeReviewReq = (id: number, params: {
    APCT: boolean;
    SA100: boolean;
    SA800: boolean;
    VAT: boolean;
  }) => ApiUtility.post(`${this.route}/${id}/updateReviewReq`, params);

  getPracticeFeatureDetails = (id: number) => ApiUtility.get(`${this.route}/${id}/practiceFeature`);

  updatePracticeFeatures = (id: number, params: IPracticeAppFeature) => ApiUtility.post(`${this.route}/${id}/updatePracticeFeatures`, params);

  updatePracticeProperties = (id: number, par: {
    params: IPracticeParams;
    appSubFeature: IPracticeAppSubFeature;
    CreditNoteReviewAmount: number;
    ClientReviewerPracticeProfile: {
      userOption: ClientReviewerUserOptionsType;
    }
    DueDateConfig: {
      number: number;
      period: number;
    },
    assignMissedCallUserOption: DefaultMissCallAgent;
  }) => ApiUtility.post(`${this.route}/${id}/updatePracticeProperties`, par);

  getFeaturePracticeDetails = (id: any) =>
    ApiUtility.get(`${this.route}/${id}/features`);

  updatePracticeDetails = (id: any, type: PracticeUpdateTypes, params: any) =>
    ApiUtility.post(`${this.route}/update/${id}/${type}`, params);

  addPractices = (params: any, cloneId?: number) => ApiUtility.post(`${this.route}/addpractices?cloneId=${cloneId ? cloneId : 0}`, params);

  getCloneProgress = (id: number) => ApiUtility.get(`${this.route}/cloneprogress/${id}`);

  markAsInactive = (id: number, status: any) => ApiUtility.post(`${this.route}/toggleStatus?id=${id}&status=${status}`, {});

  getUserData = () => ApiUtility.get(`${this.route}/PracticeDetails`);

  getPractice = () => ApiUtility.get(`${this.route}/PracticeList`);

  getSubmisionData = (practiceId: any) => ApiUtility.get(`${this.route}/SubmissionDetails?practiceId=${practiceId}`);

  getInvoiceData = (practiceId: any) => ApiUtility.get(`${this.route}/InvoiceDetails?practiceId=${practiceId}`);

  search = (practiceId: number, term?: string, page?: number) => {
    if (!term || term.length <= 0) return [];

    if (!page || page < 0) page = 0;

    return ApiUtility.getResult(`${this.route}/${practiceId}/search`, {
      practiceId,
      term,
      page,
      length: 30,

    });
  };
}

export default new PracticesService();
